import {
  Button,
  Dialog,
  Field,
  Input,
  Select,
  Portal,
  Stack,
  Text,
  Textarea,
  createListCollection,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError, useHttp } from '@mono/http-hook';
import { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type FormValues = {
  name: string;
  mimeType: string;
  data?: string;
};

type CreateFileButtonProps = {
  organizationId: string;
  projectId: string;
  folderPath: string;
  onCreated: () => void;
  isDisabled?: boolean;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const CreateFileButton: FC<CreateFileButtonProps> = ({
  organizationId,
  projectId,
  folderPath,
  onCreated,
  isDisabled,
}) => {
  const http = useHttp();
  const [isOpen, setIsOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const allowedMimeTypes = createListCollection({
    items: [
      { label: 'JSON', value: 'application/json' },
      { label: 'HTML', value: 'text/html' },
      { label: 'CSS', value: 'text/css' },
      { label: 'JavaScript', value: 'application/javascript' },
      { label: 'SVG', value: 'image/svg+xml' },
      { label: 'XML', value: 'application/xml' },
      { label: 'Text', value: 'text/plain' },
    ],
  });

  const { register, handleSubmit, reset, watch, setValue, formState } =
    useForm<FormValues>({
      defaultValues: {
        name: '',
        mimeType: allowedMimeTypes.items[0]?.value ?? '',
        data: '',
      },
    });

  const closeDialog = () => {
    setIsOpen(false);
    setServerError(null);
    reset({
      name: '',
      mimeType: allowedMimeTypes.items[0]?.value ?? '',
      data: '',
    });
  };

  const openDialog = () => {
    if (isDisabled) return;
    setIsOpen(true);
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);

    try {
      await http.post(
        `/api/v1/organizations/${organizationId}/projects/${projectId}/file-tree/files`,
        {
          folderPath,
          name: values.name.trim(),
          mimeType: values.mimeType,
          data: values.data ?? '',
        },
        { withCredentials: true }
      );

      toaster.create({
        type: 'success',
        title: 'File created',
        description: `${values.name.trim()} was added to this folder.`,
      });

      onCreated();
      closeDialog();
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to create file.');
      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to create file',
        description: message,
      });
    }
  };

  return (
    <>
      <Button
        size="sm"
        colorPalette="primary"
        onClick={openDialog}
        disabled={isDisabled}
      >
        New file
      </Button>
      <Dialog.Root
        open={isOpen}
        onOpenChange={({ open }) => {
          if (!open) closeDialog();
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.CloseTrigger />
              <Dialog.Header flexDirection="column" gap="1">
                <Dialog.Title>Create file</Dialog.Title>
                <Dialog.Description>
                  Add a new file to{' '}
                  <Text as="span" fontWeight="medium">
                    {folderPath || '/'}
                  </Text>
                  .
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body>
                <form id="create-file-form" onSubmit={handleSubmit(onSubmit)}>
                  <Stack gap="4">
                    <Field.Root invalid={Boolean(formState.errors.name)}>
                      <Field.Label>File name</Field.Label>
                      <Input
                        autoFocus
                        placeholder="copy.md"
                        {...register('name', {
                          required: 'File name is required.',
                          validate: (value) =>
                            value.includes('/') || value.includes('\\')
                              ? 'File name cannot contain slashes.'
                              : true,
                        })}
                      />
                      <Field.ErrorText>
                        {formState.errors.name?.message}
                      </Field.ErrorText>
                    </Field.Root>

                    <Field.Root invalid={Boolean(formState.errors.mimeType)}>
                      <Field.Label>Mime type</Field.Label>
                      <Select.Root
                        collection={allowedMimeTypes}
                        value={[watch('mimeType')]}
                        onValueChange={({ value }) =>
                          setValue('mimeType', value[0], {
                            shouldValidate: true,
                          })
                        }
                      >
                        <Select.HiddenSelect />
                        <Select.Trigger>
                          <Select.ValueText placeholder="Select mime type" />
                          <Select.Indicator />
                        </Select.Trigger>

                        <Select.Positioner>
                          <Select.Content>
                            {allowedMimeTypes.items.map((option) => (
                              <Select.Item item={option} key={option.value}>
                                {option.label} ({option.value})
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Select.Root>
                      <Field.HelperText>
                        Choose the content type so tools handle the file
                        correctly.
                      </Field.HelperText>
                      <Field.ErrorText>
                        {formState.errors.mimeType?.message}
                      </Field.ErrorText>
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>Initial content (optional)</Field.Label>
                      <Textarea
                        rows={6}
                        placeholder="Paste starter content…"
                        {...register('data')}
                      />
                    </Field.Root>

                    {serverError ? (
                      <Text color="fg.error" fontSize="sm">
                        {serverError}
                      </Text>
                    ) : null}
                  </Stack>
                </form>
              </Dialog.Body>
              <Dialog.Footer gap="3" justifyContent="flex-end">
                <Button
                  variant="ghost"
                  onClick={closeDialog}
                  disabled={formState.isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="create-file-form"
                  loading={formState.isSubmitting}
                  colorPalette="primary"
                >
                  Create file
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};

export default CreateFileButton;
